<template>
  <ProductModal
    :open="isOpen"
    :product="viewModel"
    :currency="currency"
    :initial-selection="initialSelection"
    :initial-quantity="initialQuantity"
    :editing="editing"
    :ordering-closed="orderingClosed"
    @close="close"
    @add="handleAdd"
  />
</template>

<script>
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import ProductModal from '@/components/Product/ProductModal.vue'
import mapRelationship from '@/utils/mapRelationship'
import mapRelationships from '@/utils/mapRelationships'
import { useToasts } from '@/composables/useToasts'
import { useProductModalOpen } from '@/composables/useProductModalOpen'
import { trackProductAdd } from '@/utils/productAnalytics'
import {
  fromV1,
  cartItemVariantOptionsToSelection,
  selectionToCartItemVariantOptions,
  buildProductSlugIndex,
  cpspIdForSlug,
  slugForCpspId,
  quantityCap,
  selectionFromVariantPreferences
} from '@/models/productViewModel'
import { blankSubmittableCartItem, findMergeableCartLine } from '@/utils/submittableCartItem'
import { readProductSlug, openProductLink, closeProductLink } from '@/utils/navigation'

// A card cannot render the modal itself: ProductContainer's `overflow-hidden` clips it and a
// collapsed category unmounts it. A card also cannot provide/inject into the host, because the
// host is its cousin, not its ancestor. So the open request lives in one module-level ref and
// exactly one host per view consumes it.
const request = ref(null)

export function useProductModal() {
  return {
    request,
    openProductModal(payload) {
      request.value = payload || null
    },
    closeProductModal() {
      request.value = null
    }
  }
}

// `mapIncluded` dereferences `relationships[type].data` unguarded, so a resource the API left the
// relationship off of throws rather than returning nothing.
function included(getter, id) {
  try {
    return getter(id) || []
  } catch (error) {
    return []
  }
}

export function v1IsUpsell(store, categoryProductStorePair) {
  const category = mapRelationship(store.state, categoryProductStorePair?.relationships?.category?.data)
  return category?.attributes?.type === 'UpsellCategory'
}

// The base price is the product on its own - modifier upcharges are added by the view model, so
// anything variant-inclusive (`net_price_plus_net_variants_price`) would be counted twice.
export function v1BasePrice(store, { product, categoryProductStorePair, isUpsell = false } = {}) {
  const cpspAttributes = categoryProductStorePair?.attributes || {}

  // Keyed on the raw cents, not on `default_upsell_price`: the serializer falls that back to the
  // product's default price (category_product_store_pair.rb:169-171), so it is never absent and
  // every upsell skipped the price-list, tier and membership chain below to quote catalog. The
  // backend only short-circuits when the merchant actually set an upsell price
  // (UpsellCartItem#assign_net_prices_cents), and otherwise runs the same chain as any other line.
  if (isUpsell && cpspAttributes.default_upsell_price_cents != null) return cpspAttributes.default_upsell_price

  const productPriceListPair = mapRelationships(
    store.state,
    store.getters.currentBottle?.relationships?.product_price_list_pairs?.data
  )?.find((item) => item.relationships?.product?.data?.id?.toString() === product?.id?.toString())
  if (productPriceListPair) return productPriceListPair.attributes.price

  const membershipTierID = store.getters.currentCart?.relationships?.membership_tier?.data?.id
  const pricingTierID = store.getters.currentCart?.relationships?.pricing_tier?.data?.id
  const tierPrice = cpspAttributes.product_price_tiers?.find(
    (item) => item.membership_tier_id == membershipTierID && item.pricing_tier_id == pricingTierID
  )

  return tierPrice || product?.attributes?.default_price || null
}

/**
 * What the product would have cost without the discount it is currently carrying - the number the
 * card strikes through. Null when there is nothing to strike.
 *
 * This is the pre-BOT-1108 ProductPricing rule, kept because the strike is the whole point of an
 * upsell price: a Cookie offered at $3.00 says nothing on its own, and "$6.00 $3.00" is the offer.
 * The customer's own tier is deliberately compared against the DEFAULT tier rather than against
 * the catalog price, so a merchant whose default tier is itself below catalog does not get a
 * permanent fake sale on every card.
 */
export function v1DefaultPrice(store, { product, categoryProductStorePair, isUpsell = false } = {}) {
  const cpspAttributes = categoryProductStorePair?.attributes || {}
  if (isUpsell && cpspAttributes.default_upsell_price_cents != null) {
    return product?.attributes?.default_price || null
  }

  const defaultMembershipTierID = store.getters.membershipTiers?.find((item) => item.attributes.is_default)?.attributes
    ?.id
  const defaultPricingTierID = store.getters.pricingTiers?.find((item) => item.attributes.is_default)?.attributes?.id
  const defaultTierPrice = cpspAttributes.product_price_tiers?.find(
    (item) => item.membership_tier_id == defaultMembershipTierID && item.pricing_tier_id == defaultPricingTierID
  )

  const catalogPrice = product?.attributes?.default_price
  if (catalogPrice?.cents != null && catalogPrice.cents > (defaultTierPrice?.cents || 0)) return catalogPrice
  return defaultTierPrice || null
}

// V1 hands out JSON:API resources whose field names predate the view model (`product_name`, a
// nutrition *relationship*, attachments carrying `attributes.secure_url` directly). Resolve all of
// that here and hand `fromV1` plain values, so the model stays pure and testable.
export function buildV1ProductViewModel(store, { product, categoryProductStorePair, cartItem = null } = {}) {
  if (!product) return null

  const productAttributes = product.attributes || {}
  const cpspAttributes = categoryProductStorePair?.attributes || {}
  const isUpsell = v1IsUpsell(store, categoryProductStorePair)
  const basePrice = v1BasePrice(store, { product, categoryProductStorePair, isUpsell })

  const variants = included(store.getters.variants, product.id).map((variant) => ({
    ...variant,
    variantOptions: included(store.getters.variantOptions, variant.id)
  }))

  const attachments = included(store.getters.attachments, product.id)
    .map((attachment) => attachment?.attributes)
    .filter((attributes) => attributes?.secure_url)
    .map(({ secure_url, high_res_url }) => ({
      sources: [{ attributes: { secure_url, high_res_url: high_res_url || null } }]
    }))

  const nutrition =
    cartItem?.nutritional_information_combined ||
    mapRelationship(store.state, product.relationships?.nutritional_information?.data)?.attributes ||
    null

  // V1 keeps the two halves of the cap in different places: remaining stock on the cpsp, the
  // merchant's own limit on the product. Resolve both here and let the shared model apply the
  // tighter, the same way it does for V2.
  const maxPerOrder = quantityCap({
    inventoryRemaining: cpspAttributes.quantity_remaining,
    merchantMax: productAttributes.max_per_order,
    cartQuantity: cartItem?.quantity
  })

  return fromV1({
    product: {
      id: product.id,
      attributes: {
        ...productAttributes,
        name: productAttributes.product_name || productAttributes.name || '',
        nutritional_information: nutrition
      }
    },
    categoryProductStorePair: {
      id: categoryProductStorePair?.id,
      attributes: {
        show_prices: cpspAttributes.show_prices !== false,
        quantity_step: productAttributes.step || 1,
        max_per_order: maxPerOrder
      }
    },
    variants,
    labels: included(store.getters.labels, product.id),
    attachments,
    basePriceCents: basePrice?.cents || 0,
    showPrices: cpspAttributes.show_prices !== false,
    cartItem
  })
}

// Every pair on THIS bottle's menu, which is what scopes the slug index to one store. Reading
// `state.objects.category_product_store_pair` directly would be wrong: Vuex accumulates objects
// across a session, so a customer who visited another store first would build a wider index and
// get different - suffixed - URLs for the same products.
export function v1SlugEntries(store) {
  const pairs = mapRelationships(
    store?.state,
    store?.getters?.currentBottle?.relationships?.category_product_store_pairs?.data
  )

  return (pairs || [])
    .map((pair) => {
      const productId = pair?.relationships?.product?.data?.id
      const product = productId == null ? null : store?.state?.objects?.product?.[productId]
      return {
        cpspId: pair?.id,
        productId,
        name: product?.attributes?.product_name || product?.attributes?.name || '',
        isUpsell: v1IsUpsell(store, pair)
      }
    })
    .filter((entry) => entry.cpspId != null && entry.productId != null)
}

export function v1SlugIndex(store) {
  return buildProductSlugIndex(v1SlugEntries(store))
}

// Turns the URL's product segment back into the open request a card would have made. The slug
// resolves through the store's index to a cpsp id, and the cpsp is what carries the product
// relationship, so the lookup continues there. Returns null - never throws, never guesses - for a
// slug whose cpsp or product is not in Vuex, which is how a stale link degrades to "just the store".
export function v1OpenRequestForSlug(store, slug) {
  const cpspId = cpspIdForSlug(v1SlugIndex(store), slug)
  if (cpspId == null) return null

  const categoryProductStorePair = store?.state?.objects?.category_product_store_pair?.[cpspId]
  if (!categoryProductStorePair) return null

  const productId = categoryProductStorePair.relationships?.product?.data?.id
  const product = productId == null ? null : store?.state?.objects?.product?.[productId]
  if (!product) return null

  // `handleAdd` bails without a submittable line, and the blank one a never-added product uses is
  // minted client-side in the card container (a computed that re-mints a fresh ulid on every
  // access), so there is nothing in Vuex to look up. Prefer the real line when the product is
  // already in the cart - that is what tapping the card would have opened - and mint a blank one
  // with the same shape otherwise.
  const splitDay = store?.state?.splitDay || null
  const cpspAttributeId = categoryProductStorePair.attributes?.id ?? cpspId
  const existing = (store?.getters?.submittableCart || []).find(
    (item) =>
      String(item.category_product_store_pair_id) === String(cpspAttributeId) &&
      (!splitDay || item.fulfillment_group_date === splitDay)
  )

  return {
    productId: product.id,
    categoryProductStorePairId: categoryProductStorePair.id,
    submittableCartItem:
      existing ||
      blankSubmittableCartItem({
        categoryProductStorePairId: categoryProductStorePair.id,
        fulfillmentGroupDate: splitDay,
        isUpsell: v1IsUpsell(store, categoryProductStorePair)
      })
  }
}

// V1 has no snapshot to watch: the modal's data arrives through Vuex, so the restore has to be
// driven by the view once `getBottle` has resolved. Selection.vue calls this from `mounted`,
// beside the existing `?showCart=y` restore.
export function restoreProductModalFromRoute(store, router, route) {
  const slug = readProductSlug(route)
  if (!slug) return false

  const request = v1OpenRequestForSlug(store, slug)
  if (!request) {
    closeProductLink(router, route)
    return false
  }

  useProductModal().openProductModal(request)
  return true
}

export default {
  name: 'ProductModalHost',

  components: { ProductModal },

  setup() {
    const store = useStore()
    const { request: openRequest, openProductModal, closeProductModal } = useProductModal()
    const { addToast } = useToasts()

    const product = computed(() =>
      openRequest.value ? store.state.objects.product?.[openRequest.value.productId] : null
    )

    const categoryProductStorePair = computed(() =>
      openRequest.value
        ? store.state.objects.category_product_store_pair?.[openRequest.value.categoryProductStorePairId]
        : null
    )

    // A card that has never been added carries a locally-minted cart line that is not in the cart
    // yet, so the snapshot the card handed over is the only copy. Once it is in the cart, prefer
    // the live line so the modal re-opens on the server's current quantity.
    const cartItem = computed(() => {
      const snapshot = openRequest.value?.submittableCartItem
      if (!snapshot) return null
      const live = (store.getters.submittableCart || []).find((item) => item.frontend_key === snapshot.frontend_key)
      return live || snapshot
    })

    const isUpsell = computed(() => v1IsUpsell(store, categoryProductStorePair.value))

    const basePrice = computed(() =>
      product.value
        ? v1BasePrice(store, {
            product: product.value,
            categoryProductStorePair: categoryProductStorePair.value,
            isUpsell: isUpsell.value
          })
        : null
    )

    const viewModel = computed(() =>
      buildV1ProductViewModel(store, {
        product: product.value,
        categoryProductStorePair: categoryProductStorePair.value,
        cartItem: cartItem.value
      })
    )

    return {
      openRequest,
      openProductModal,
      closeProductModal,
      addToast,
      product,
      cartItem,
      isUpsell,
      basePrice,
      viewModel
    }
  },

  computed: {
    isOpen() {
      return Boolean(this.openRequest && this.viewModel)
    },

    slugIndex() {
      return v1SlugIndex(this.$store)
    },

    // The slug the URL should carry while this request is open. It comes from the store's index
    // rather than from this product alone, because whether it needs a disambiguating id depends on
    // what else is on the menu.
    openProductSlug() {
      if (!this.isOpen) return null
      return slugForCpspId(this.slugIndex, this.openRequest?.categoryProductStorePairId)
    },

    currency() {
      return this.basePrice?.currency_iso || 'USD'
    },

    orderingClosed() {
      const status = this.$store.getters.currentBottle?.attributes?.status_for_placing_orders?.status
      return Boolean(status) && status !== 'open'
    },

    // The package the bottle is on, if any. Its variant preferences seed a blank configuration.
    //
    // V1 sends them as generic key/value metadata rather than as named fields: the KEY is the
    // group name and the VALUE is the option name (VariantPreferenceMetadatum#variant_name /
    // #variant_option_name read exactly those two columns). V2's snapshot has already done this
    // translation, so it is done here rather than inside the shared model.
    variantPreferences() {
      const selectedPackage = mapRelationship(
        this.$store.state,
        this.$store.getters.currentBottle?.relationships?.package?.data
      )
      return mapRelationships(this.$store.state, selectedPackage?.relationships?.variant_preference_metadata?.data)
        .map((metadatum) => metadatum?.attributes)
        .filter(Boolean)
        .map((attributes) => ({
          variant_name: attributes.key,
          variant_option_name: attributes.string_value || attributes.content
        }))
    },

    initialSelection() {
      const fromCartItem = cartItemVariantOptionsToSelection(this.cartItem?.cart_item_variant_options_attributes || [])
      // Only a blank configuration takes the package's preferences. An existing line already
      // carries the customer's own answer, and overwriting it on re-open would undo an override
      // every time they looked at the line again.
      if (this.editing) return fromCartItem
      return { ...selectionFromVariantPreferences(this.viewModel, this.variantPreferences), ...fromCartItem }
    },

    initialQuantity() {
      return Math.max(1, Number(this.cartItem?.quantity) || 1)
    },

    editing() {
      return Number(this.cartItem?.quantity) > 0
    }
  },

  watch: {
    // The upsell modal hides itself while a product modal is on top of it, rather than stacking two
    // dialogs. Reported from `isOpen` rather than from the open request, so a request that never
    // resolves a product does not hide the upsell modal behind nothing.
    isOpen: {
      handler(open) {
        this.setProductModalOpen(open)
      },
      immediate: true
    },
    // Cards open the modal by writing straight to the module-level request, bypassing this host
    // entirely, so the URL has to follow the request rather than the other way round. Only the
    // open direction lives here: closing goes through `close()`, which owns stripping the param.
    // Splitting it that way is what keeps a route-driven close from stripping the param twice.
    openProductSlug(slug) {
      if (slug) openProductLink(this.$router, this.$route, slug)
    },

    '$route.params.productSlug'() {
      this.syncWithRoute()
    }
  },

  // The open request is a module-level ref that outlives any one host, so a modal left open on a
  // previous visit to this view would re-render here with nothing in the URL to explain it. The
  // URL wins. A request that IS in the URL is left alone - Vuex is still empty at this point, so
  // resolving it here would only find nothing; Selection.vue restores it after `getBottle`.
  mounted() {
    if (readProductSlug(this.$route) == null) this.closeProductModal()
  },

  beforeUnmount() {
    this.setProductModalOpen(false)
  },

  methods: {
    setProductModalOpen(open) {
      useProductModalOpen().setProductModalOpen(open)
    },

    close() {
      this.closeProductModal()
      closeProductLink(this.$router, this.$route)
    },

    // Reconciles the modal with the URL's product segment after a Back/Forward or any external
    // push. The initial restore is NOT here - at mount Vuex is still empty, so Selection.vue runs
    // it from `mounted` once `getBottle` has resolved (see `restoreProductModalFromRoute`).
    syncWithRoute() {
      const slug = readProductSlug(this.$route)

      if (!slug) {
        if (this.openRequest) this.closeProductModal()
        return
      }

      const cpspId = cpspIdForSlug(this.slugIndex, slug)
      if (
        cpspId != null &&
        this.openRequest &&
        String(this.openRequest.categoryProductStorePairId) === String(cpspId)
      ) {
        return
      }

      const request = v1OpenRequestForSlug(this.$store, slug)
      if (!request) {
        if (this.openRequest) this.closeProductModal()
        closeProductLink(this.$router, this.$route)
        return
      }

      this.openProductModal(request)
    },

    handleAdd({ selection, quantity }) {
      if (this.orderingClosed) return
      const existing = this.cartItem
      if (!existing) return

      // V1's inline options wrote straight through the prop; a clone keeps the
      // dispatch from mutating the cart line the card is still rendering.
      const merged = {
        ...JSON.parse(JSON.stringify(existing)),
        quantity,
        // V1 stores these ids as strings everywhere else in the submittable cart; keep the shape
        // identical so a modal-built line and a server-derived line stay comparable.
        cart_item_variant_options_attributes: selectionToCartItemVariantOptions(selection).map((row) => ({
          variant_option_id: String(row.variant_option_id),
          variant_id: String(row.variant_id),
          quantity: row.quantity
        }))
      }

      const wasEditing = this.editing

      // Adding a configuration the cart already holds raises that line rather than printing a
      // second card identical to it. Only on add: re-configuring an existing line into another
      // one's shape would make the line the customer was editing vanish under them.
      const mergeable = wasEditing
        ? null
        : findMergeableCartLine(this.$store.getters.submittableCart, merged, {
            ceiling: this.viewModel?.maxPerOrder,
            addedQuantity: quantity
          })

      this.trackAdd(wasEditing ? quantity - (Number(existing.quantity) || 0) : quantity)
      this.$store.dispatch('updateSubmittableCart', {
        submittableCartItem: mergeable ? { ...mergeable.line, quantity: mergeable.quantity } : merged
      })
      this.addToast({
        message: wasEditing ? 'Cart updated' : 'Added to cart',
        type: 'success'
      })
      this.close()
    },

    trackAdd(quantity) {
      trackProductAdd(this.$store, {
        isUpsell: this.isUpsell,
        quantity,
        productId: this.product?.attributes?.id ?? this.product?.id,
        productName: this.product?.attributes?.product_name
      })
    }
  }
}
</script>
